(() => {
  'use strict';

  const DEFAULT_LABELS = Object.freeze({
    add: 'In winkelmand',
    soldOut: 'Uitverkocht',
    inStock: 'Op voorraad',
    unavailable: 'Niet beschikbaar'
  });

  function parseConfig(root) {
    const sectionId = root?.dataset.sectionId;
    const node = root?.querySelector('[data-cv6-commerce-config]')
      || (sectionId ? document.querySelector(`[data-cv6-commerce-config][data-section-id="${CSS.escape(sectionId)}"]`) : null);
    if (!node) return {};
    try {
      return JSON.parse(node.value || node.textContent || '{}');
    } catch (error) {
      console.error('[CV6CommerceCore] Invalid commerce config', error);
      return {};
    }
  }

  function money(cents, config) {
    const value = Number(cents || 0) / 100;
    try {
      return new Intl.NumberFormat(config.locale || 'nl-NL', {
        style: 'currency',
        currency: config.currency || 'EUR'
      }).format(value);
    } catch (_) {
      return `€ ${value.toFixed(2).replace('.', ',')}`;
    }
  }

  function mount(root, suppliedConfig) {
    if (!root || root.dataset.cv6CommerceCoreMounted === 'true') return root?.__cv6CommerceCore || null;

    const config = { ...parseConfig(root), ...(suppliedConfig || {}) };
    const variants = Array.isArray(config.variants) ? config.variants : [];
    const labels = { ...DEFAULT_LABELS, ...(config.labels || {}) };
    const form = root.querySelector('[data-product-form]');
    const idInput = root.querySelector('[data-variant-id]') || form?.querySelector('[name="id"]');
    const quantity = root.querySelector('[data-qty]') || form?.querySelector('[name="quantity"]');
    const addButton = root.querySelector('[data-atc]') || form?.querySelector('[type="submit"]');
    const optionButtons = [...root.querySelectorAll('[data-option-position]')];
    const selected = Array.isArray(config.selected) ? config.selected.slice() : [];

    if (!form || !idInput || !quantity || !addButton || !variants.length) return null;

    const normalizeQuantity = () => {
      const min = Math.max(1, Number(quantity.min) || 1);
      const max = Math.max(min, Number(quantity.max) || 99);
      quantity.value = String(Math.min(max, Math.max(min, Number(quantity.value) || min)));
      return Number(quantity.value);
    };

    const variantById = id => variants.find(variant => String(variant.id) === String(id));

    const updateOptionAvailability = () => optionButtons.forEach(button => {
      const index = Number(button.dataset.optionPosition) - 1;
      const exists = variants.some(variant => variant.options?.[index] === button.dataset.optionValue);
      const available = variants.some(variant => variant.available && variant.options?.[index] === button.dataset.optionValue);
      button.disabled = false;
      button.dataset.available = String(available);
      button.classList.toggle('is-unavailable', exists && !available);
      button.hidden = !exists;
    });

    const syncPriceAndCta = variant => {
      const count = normalizeQuantity();
      const price = root.querySelector('[data-price]');
      const compare = root.querySelector('[data-compare]');
      const availability = root.querySelector('[data-availability]');
      const availabilityText = availability?.querySelector('[data-availability-text]');
      const buttonLabel = addButton.querySelector('span') || addButton;

      if (price) price.textContent = money(variant.price, config);
      if (compare) {
        compare.hidden = !(Number(variant.compare_at_price) > Number(variant.price));
        if (!compare.hidden) compare.textContent = money(variant.compare_at_price, config);
      }
      if (availability) availability.classList.toggle('is-unavailable', !variant.available);
      if (availabilityText) availabilityText.textContent = variant.available ? labels.inStock : labels.soldOut;
      addButton.disabled = !variant.available;
      addButton.setAttribute('aria-disabled', String(!variant.available));
      buttonLabel.textContent = variant.available ? `${labels.add} · ${money(Number(variant.price) * count, config)}` : labels.soldOut;
      root.classList.toggle('is-variant-unavailable', !variant.available);
    };

    const setVariant = (variant, source = 'core') => {
      if (!variant) return false;
      variant.options?.forEach((value, index) => {
        selected[index] = value;
        optionButtons
          .filter(button => Number(button.dataset.optionPosition) === index + 1)
          .forEach(button => {
            const active = button.dataset.optionValue === value;
            button.setAttribute('aria-pressed', String(active));
            button.classList.toggle('is-active', active);
          });
      });
      idInput.disabled = false;
      idInput.value = String(variant.id);
      syncPriceAndCta(variant);
      try {
        const url = new URL(window.location.href);
        url.searchParams.set('variant', String(variant.id));
        history.replaceState({}, '', url);
      } catch (_) {}
      root.dispatchEvent(new CustomEvent('cv6:core-variant-change', { bubbles: true, detail: { variant, source } }));
      return true;
    };

    const setQuantity = (next, source) => {
      quantity.value = String(next);
      const count = normalizeQuantity();
      const variant = variantById(idInput.value);
      if (variant) syncPriceAndCta(variant);
      root.dispatchEvent(new CustomEvent('cv6:core-quantity-change', { bubbles: true, detail: { quantity: count, source } }));
      return count;
    };

    optionButtons.forEach(button => button.addEventListener('click', () => {
      if (button.hidden) return;
      const index = Number(button.dataset.optionPosition) - 1;
      if (index < 0) return;
      const candidate = selected.slice();
      candidate[index] = button.dataset.optionValue;
      const exact = variants.find(item => item.options?.every((value, optionIndex) => value === candidate[optionIndex]));
      const variant = (exact?.available ? exact : null)
        || variants.find(item => item.available && item.options?.[index] === candidate[index])
        || exact;
      setVariant(variant, 'option');
    }));

    root.querySelector('[data-qty-minus]')?.addEventListener('click', () => setQuantity(Number(quantity.value) - 1, 'minus'));
    root.querySelector('[data-qty-plus]')?.addEventListener('click', () => setQuantity(Number(quantity.value) + 1, 'plus'));
    quantity.addEventListener('input', () => setQuantity(quantity.value, 'input'));
    quantity.addEventListener('change', () => setQuantity(quantity.value, 'change'));
    idInput.addEventListener('change', () => setVariant(variantById(idInput.value), 'input'));

    const active = variantById(idInput.value) || variants.find(variant => variant.available) || variants[0];
    if (active) setVariant(active, 'initialization');
    updateOptionAvailability();

    const controller = { config, variants, setVariant, setQuantity, variantById };
    root.dataset.cv6CommerceCoreMounted = 'true';
    root.__cv6CommerceCore = controller;
    return controller;
  }

  const api = { mount, money, parseConfig };
  if (typeof window !== 'undefined') window.CV6CommerceCore = api;
  if (typeof module !== 'undefined' && module.exports) module.exports = api;
})();
